import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import { ClinicMapLocation } from '@chiroup/core/types/ClinicMapLocation.type';
import { SlidersHorizontal as AdjustmentsHorizontalIcon } from 'lucide-react';
import { memo } from 'react';
import { ColorByOption } from './ClinicMapView';
import {
  COLOR_BY_OPTIONS,
  CustomColors,
  FilterOption,
  getStateName,
  PLAN_OPTIONS,
  POST_TYPE_OPTIONS,
  POSTING_PLATFORM_OPTIONS,
  POSTING_SCHEDULE_OPTIONS,
  StateInfo,
  STATUS_OPTIONS,
} from './constants';
import FilterSection from './FilterSection';
import { FilterRelations } from './useClinicMapFilters';

interface FilterPanelProps {
  locations: ClinicMapLocation[];
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  activeFilterCount: number;
  // Filter states
  selectedPlans: string[];
  setSelectedPlans: (plans: string[]) => void;
  selectedStatus: string[];
  setSelectedStatus: (status: string[]) => void;
  selectedSchedules: string[];
  setSelectedSchedules: (schedules: string[]) => void;
  selectedPlatforms: string[];
  setSelectedPlatforms: (platforms: string[]) => void;
  selectedPostTypes: string[];
  setSelectedPostTypes: (postTypes: string[]) => void;
  selectedStatesFilter: string[];
  setSelectedStatesFilter: (states: string[]) => void;
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  // Filter mode
  filterMode: 'and' | 'or' | 'custom';
  setFilterMode: (mode: 'and' | 'or' | 'custom') => void;
  filterRelations: FilterRelations;
  setFilterRelations: (relations: FilterRelations) => void;
  // Color settings
  colorBy: ColorByOption;
  setColorBy: (colorBy: ColorByOption) => void;
  customColors: CustomColors;
  onColorChange: (category: ColorByOption, value: string, color: string) => void;
  // State filter data
  filteredFilterStates: StateInfo[];
  statesFilterSearch: string;
  setStatesFilterSearch: (search: string) => void;
}

const FilterPanel = ({
  locations,
  isOpen,
  onClose,
  onToggle,
  activeFilterCount,
  selectedPlans,
  setSelectedPlans,
  selectedStatus,
  setSelectedStatus,
  selectedSchedules,
  setSelectedSchedules,
  selectedPlatforms,
  setSelectedPlatforms,
  selectedPostTypes,
  setSelectedPostTypes,
  selectedStatesFilter,
  setSelectedStatesFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  filterMode,
  setFilterMode,
  filterRelations,
  setFilterRelations,
  colorBy,
  setColorBy,
  customColors,
  onColorChange,
  filteredFilterStates,
  statesFilterSearch,
  setStatesFilterSearch,
}: FilterPanelProps) => {
  const togglePlan = (plan: string) => {
    setSelectedPlans(
      selectedPlans.includes(plan)
        ? selectedPlans.filter(p => p !== plan)
        : [...selectedPlans, plan]
    );
  };

  const toggleStatus = (status: string) => {
    setSelectedStatus(
      selectedStatus.includes(status)
        ? selectedStatus.filter(s => s !== status)
        : [...selectedStatus, status]
    );
  };

  const toggleSchedule = (schedule: string) => {
    setSelectedSchedules(
      selectedSchedules.includes(schedule)
        ? selectedSchedules.filter(s => s !== schedule)
        : [...selectedSchedules, schedule]
    );
  };

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(
      selectedPlatforms.includes(platform)
        ? selectedPlatforms.filter(p => p !== platform)
        : [...selectedPlatforms, platform]
    );
  };

  const togglePostType = (postType: string) => {
    setSelectedPostTypes(
      selectedPostTypes.includes(postType)
        ? selectedPostTypes.filter(t => t !== postType)
        : [...selectedPostTypes, postType]
    );
  };

  const toggleStateFilter = (state: string) => {
    setSelectedStatesFilter(
      selectedStatesFilter.includes(state)
        ? selectedStatesFilter.filter(s => s !== state)
        : [...selectedStatesFilter, state]
    );
  };

  const clearAllFilters = () => {
    setSelectedPlans(PLAN_OPTIONS.map(p => p.value));
    setSelectedStatus(STATUS_OPTIONS.map(s => s.value));
    setSelectedSchedules(POSTING_SCHEDULE_OPTIONS.map(s => s.value));
    setSelectedPlatforms(POSTING_PLATFORM_OPTIONS.map(p => p.value));
    setSelectedPostTypes(POST_TYPE_OPTIONS.map(t => t.value));
    setSelectedStatesFilter([]);
    setStartDate('');
    setEndDate('');
  };

  const getCounts = (
    options: FilterOption[],
    getMatch: (loc: ClinicMapLocation, opt: FilterOption) => boolean
  ) => {
    return (opt: FilterOption) => locations.filter(l => getMatch(l, opt)).length;
  };

  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`inline-flex items-center gap-2 rounded-md border bg-white px-4 py-2 text-sm font-medium shadow-md hover:bg-gray-50 ${
          activeFilterCount > 0
            ? 'border-primary-500 text-primary-700'
            : 'border-gray-300 text-gray-700'
        }`}
      >
        <AdjustmentsHorizontalIcon className="h-5 w-5" />
        Filters
        {activeFilterCount > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-xs text-white">
            {activeFilterCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[999]" onClick={onClose} />
          <div className="absolute right-0 z-[1001] mt-2 w-80 rounded-lg border border-gray-200 bg-white shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <span className="font-medium text-gray-900">Filters</span>
              <div className="flex items-center gap-2">
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-primary-600 hover:text-primary-700"
                  >
                    Clear all
                  </button>
                )}
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                  <Icon icon={Icons.close} className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Filter Sections */}
            <div className="max-h-[60vh] overflow-y-auto p-4">
              {/* Filter Mode Toggle */}
              <div className="mb-4 border-b border-gray-100 pb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">Filter logic</label>
                <div className="flex rounded-md border border-gray-300">
                  <button
                    onClick={() => setFilterMode('and')}
                    className={`flex-1 px-2 py-1.5 text-sm font-medium transition-colors ${
                      filterMode === 'and'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } rounded-l-md`}
                  >
                    Match All
                  </button>
                  <button
                    onClick={() => setFilterMode('or')}
                    className={`flex-1 px-2 py-1.5 text-sm font-medium transition-colors ${
                      filterMode === 'or'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } border-l border-gray-300`}
                  >
                    Match Any
                  </button>
                  <button
                    onClick={() => setFilterMode('custom')}
                    className={`flex-1 px-2 py-1.5 text-sm font-medium transition-colors ${
                      filterMode === 'custom'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    } rounded-r-md border-l border-gray-300`}
                  >
                    Custom
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  {filterMode === 'and'
                    ? 'Show clinics matching all selected filters'
                    : filterMode === 'or'
                      ? 'Show clinics matching any selected filter'
                      : 'Define AND/OR logic per filter group'}
                </p>
              </div>

              {/* Color By Selector */}
              <div className="mb-4 border-b border-gray-100 pb-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Color pins by
                </label>
                <select
                  value={colorBy}
                  onChange={e => setColorBy(e.target.value as ColorByOption)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {COLOR_BY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <FilterSection
                title="Status"
                options={STATUS_OPTIONS}
                selected={selectedStatus}
                onToggle={toggleStatus}
                onSelectAll={() => setSelectedStatus(STATUS_OPTIONS.map(s => s.value))}
                onClear={() => setSelectedStatus([])}
                getCounts={getCounts(STATUS_OPTIONS, (l, opt) =>
                  opt.value === 'churned' ? l.churned : !l.churned
                )}
                isCustomMode={filterMode === 'custom'}
                relation={filterRelations.status}
                onRelationChange={r => setFilterRelations({ ...filterRelations, status: r })}
                customColors={customColors.status}
                onColorChange={(value, color) => onColorChange('status', value, color)}
              />

              {/* Plan */}
              <FilterSection
                title="Plan"
                options={PLAN_OPTIONS}
                selected={selectedPlans}
                onToggle={togglePlan}
                onSelectAll={() => setSelectedPlans(PLAN_OPTIONS.map(p => p.value))}
                onClear={() => setSelectedPlans([])}
                getCounts={getCounts(
                  PLAN_OPTIONS,
                  (l, opt) => (l.plan?.toLowerCase() || 'unknown') === opt.value
                )}
                isCustomMode={filterMode === 'custom'}
                relation={filterRelations.plan}
                onRelationChange={r => setFilterRelations({ ...filterRelations, plan: r })}
                customColors={customColors.plan}
                onColorChange={(value, color) => onColorChange('plan', value, color)}
              />

              {/* Subscription Date Range */}
              <div className="mb-4 border-b border-gray-100 pb-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Subscription Date</span>
                  <div className="flex items-center gap-2">
                    {filterMode === 'custom' && (
                      <div className="flex rounded border border-gray-200 text-xs">
                        <button
                          onClick={() => setFilterRelations({ ...filterRelations, date: 'and' })}
                          className={`px-1.5 py-0.5 transition-colors ${
                            filterRelations.date === 'and'
                              ? 'bg-primary-100 text-primary-700 font-medium'
                              : 'text-gray-500 hover:bg-gray-50'
                          } rounded-l`}
                        >
                          AND
                        </button>
                        <button
                          onClick={() => setFilterRelations({ ...filterRelations, date: 'or' })}
                          className={`px-1.5 py-0.5 transition-colors ${
                            filterRelations.date === 'or'
                              ? 'bg-primary-100 text-primary-700 font-medium'
                              : 'text-gray-500 hover:bg-gray-50'
                          } rounded-r border-l border-gray-200`}
                        >
                          OR
                        </button>
                      </div>
                    )}
                    {(startDate || endDate) && (
                      <button
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">From</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-gray-500">To</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              {/* Posting Schedule */}
              <FilterSection
                title="Posting Schedule"
                options={POSTING_SCHEDULE_OPTIONS}
                selected={selectedSchedules}
                onToggle={toggleSchedule}
                onSelectAll={() => setSelectedSchedules(POSTING_SCHEDULE_OPTIONS.map(s => s.value))}
                onClear={() => setSelectedSchedules([])}
                getCounts={getCounts(POSTING_SCHEDULE_OPTIONS, (l, opt) =>
                  opt.value === 'not_set' ? !l.postingSchedule : l.postingSchedule === opt.value
                )}
                isCustomMode={filterMode === 'custom'}
                relation={filterRelations.schedule}
                onRelationChange={r => setFilterRelations({ ...filterRelations, schedule: r })}
                customColors={customColors.schedule}
                onColorChange={(value, color) => onColorChange('schedule', value, color)}
              />

              {/* Posting Platforms */}
              <FilterSection
                title="Posting Platforms"
                options={POSTING_PLATFORM_OPTIONS}
                selected={selectedPlatforms}
                onToggle={togglePlatform}
                onSelectAll={() => setSelectedPlatforms(POSTING_PLATFORM_OPTIONS.map(p => p.value))}
                onClear={() => setSelectedPlatforms([])}
                getCounts={getCounts(POSTING_PLATFORM_OPTIONS, (l, opt) =>
                  opt.value === 'not_set'
                    ? !l.postingPlatforms
                    : l.postingPlatforms?.includes(opt.value) || false
                )}
                isCustomMode={filterMode === 'custom'}
                relation={filterRelations.platform}
                onRelationChange={r => setFilterRelations({ ...filterRelations, platform: r })}
                customColors={customColors.platform}
                onColorChange={(value, color) => onColorChange('platform', value, color)}
              />

              {/* Post Type */}
              <FilterSection
                title="Post Type"
                options={POST_TYPE_OPTIONS}
                selected={selectedPostTypes}
                onToggle={togglePostType}
                onSelectAll={() => setSelectedPostTypes(POST_TYPE_OPTIONS.map(t => t.value))}
                onClear={() => setSelectedPostTypes([])}
                getCounts={getCounts(POST_TYPE_OPTIONS, (l, opt) =>
                  opt.value === 'not_set' ? !l.postType : l.postType === opt.value
                )}
                isCustomMode={filterMode === 'custom'}
                relation={filterRelations.postType}
                onRelationChange={r => setFilterRelations({ ...filterRelations, postType: r })}
                customColors={customColors.postType}
                onColorChange={(value, color) => onColorChange('postType', value, color)}
              />

              {/* Geographic Filter */}
              <div className="mb-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">States/Provinces</span>
                  <div className="flex items-center gap-2">
                    {filterMode === 'custom' && (
                      <div className="flex rounded border border-gray-200 text-xs">
                        <button
                          onClick={() => setFilterRelations({ ...filterRelations, states: 'and' })}
                          className={`px-1.5 py-0.5 transition-colors ${
                            filterRelations.states === 'and'
                              ? 'bg-primary-100 text-primary-700 font-medium'
                              : 'text-gray-500 hover:bg-gray-50'
                          } rounded-l`}
                        >
                          AND
                        </button>
                        <button
                          onClick={() => setFilterRelations({ ...filterRelations, states: 'or' })}
                          className={`px-1.5 py-0.5 transition-colors ${
                            filterRelations.states === 'or'
                              ? 'bg-primary-100 text-primary-700 font-medium'
                              : 'text-gray-500 hover:bg-gray-50'
                          } rounded-r border-l border-gray-200`}
                        >
                          OR
                        </button>
                      </div>
                    )}
                    {selectedStatesFilter.length > 0 && (
                      <button
                        onClick={() => setSelectedStatesFilter([])}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                {selectedStatesFilter.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {selectedStatesFilter.map(state => (
                      <span
                        key={state}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-700"
                      >
                        {getStateName(state)} ({state})
                        <button
                          onClick={() => toggleStateFilter(state)}
                          className="hover:text-primary-900"
                        >
                          <Icon icon={Icons.close} className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="relative mb-2">
                  <Icon
                    icon={Icons.search}
                    className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search states..."
                    value={statesFilterSearch}
                    onChange={e => setStatesFilterSearch(e.target.value)}
                    className="w-full rounded border border-gray-300 py-1.5 pl-7 pr-7 text-sm placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  {statesFilterSearch && (
                    <button
                      onClick={() => setStatesFilterSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <Icon icon={Icons.close} className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto rounded border border-gray-200">
                  {filteredFilterStates.length === 0 ? (
                    <div className="px-2 py-2 text-center text-sm text-gray-500">
                      No states found
                    </div>
                  ) : (
                    filteredFilterStates.map(state => (
                      <label
                        key={state.abbrev}
                        className="flex cursor-pointer items-center gap-2 px-2 py-1.5 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStatesFilter.includes(state.abbrev)}
                          onChange={() => toggleStateFilter(state.abbrev)}
                          className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="flex-1 text-sm text-gray-700">{state.fullName}</span>
                        <span className="text-xs text-gray-400">{state.count}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {selectedStatesFilter.length === 0
                    ? 'Select states to filter'
                    : `${selectedStatesFilter.length} selected`}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default memo(FilterPanel);
